import React from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';
import { useLocation } from 'react-router-dom';
import { makePath } from 'apps/Pdf/helpers/helpers';

import Grid from '@material-ui/core/Grid';

import { sqlDateToLabel, formatAmount, formatAmountValue, fractionToPercent } from 'helpers/helpers';
import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import P from 'apps/Pdf/components/common/P';
import Spacer from 'apps/Pdf/components/common/Spacer';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import BasicData from 'apps/Pdf/components/BasicData/BasicData';
import Glossary from 'apps/Pdf/components/Glossary/Glossary';
import Disclaimer from 'apps/Pdf/components/Disclaimer/Disclaimer';
import MiniBucket from 'apps/Pdf/components/Bucket/MiniBucket';
import PlanBucket from 'apps/Pdf/components/Bucket/PlanBucket';
import Deposits from 'apps/Pdf/components/DespositsAndReserves/Deposits';
import GeneralDeposit from 'apps/Pdf/components/DespositsAndReserves/GeneralDeposit';
import Rent from 'apps/Pdf/components/Rent/Rent';
import Loading from 'apps/Pdf/components/common/Loading';
import FinancesOverview from './components/FinancesOverview';
import CashMovementsTable from './components/CashMovementsTable';
import FeeMovementsTable from './components/FeeMovementsTable';
import FeesEffect from './components/FeesEffect';
import BucketDetail from './components/BucketDetail';
import LineGraph from 'apps/Pdf/components/Charts/DevelopmentLineGraph';
import InterestsTable from './components/InterestsTable';

import PlanChart from 'apps/Pdf/components/Charts/PlanChart';
import Targets from 'apps/Pdf/components/Targets/Targets';
import FutureDeposits from 'apps/Pdf/components/DespositsAndReserves/FutureDeposits';
import Reserves from 'apps/Pdf/components/DespositsAndReserves/Reserves';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles, { colors as colorDef, colorOpacity as colorOpacityDef } from 'apps/Pdf/styles/colors';
import { MonthlyStatementContent } from './StatementInterface';
import { PersonType, Currency } from 'enums';
import { PdfCategory } from '../Interfaces';

const Statement = (params: PdfContentParams) => {
  const location = useLocation();
  const monthly =
    params.type === 'MONTHLY_STATEMENT' || makePath(TPL_MONTHLY_STATEMENT) === location.pathname.slice(0, -1);

  const pdfContent = usePdfContent<MonthlyStatementContent>({
    ...params,
    category: PdfCategory.STATEMENT,
    templateType: monthly ? TPL_MONTHLY_STATEMENT : TPL_YEARLY_STATEMENT,
    //type: params.preview ? params.type : undefined,
  });

  const invalidData = pdfContent?.data && pdfContent.data.investmentPlan.externalProductData === undefined;

  const styles = useGeneralStyles();
  const { t } = useTranslation();

  const data = pdfContent?.data ? pdfContent.data : ({} as MonthlyStatementContent);
  const currency = pdfContent?.data ? pdfContent.data.currency : Currency.CZK;
  const rent = !!data.investmentPlan?.rentApplied;
  const complete = !data.investmentPlan?.portfolioOnly;
  const withHistory = data.history?.withHistory;

  const lastDate = data.investmentPlan?.chart?.endDate || '';
  const lastValue = data.investmentPlan?.chart?.valueAtEnd?.value || 0;

  const inflationSentence = data?.investmentPlan?.chart.inflation.enabled
    ? 'v současných (reálných) hodnotách'
    : 'v budoucích (nominálních) hodnotách';

  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });

  if (pdfContent && invalidData)
    return (
      <PdfContent errors={pdfContent.errors} className={colors.root} onload afterLoad={params.afterLoad} empty>
        <P className={`${styles.largeText} ${styles.center}`}>
          Omlouváme se, náhled se nepodařilo zobrazit. Report si můžete stáhnout a pak zobrazit
        </P>
      </PdfContent>
    );

  return pdfContent ? (
    <PdfContent errors={pdfContent.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>
        {data.type === 'MONTH' ? 'Měsíční' : 'Roční'} report z účtu - {sqlDateToLabel(data.date, 'M/yyyy')}
      </Title>

      <BasicData data={pdfContent.data!.header} />

      <H1>Aktuální stav k {sqlDateToLabel(data.date, 'd. M. yyyy')}</H1>

      <div className={`${styles.threeColumns}`}>
        {complete || withHistory ? (
          <>
            <MiniBucket
              term={withHistory ? 'default' : 'long_term'}
              data={data.current.buckets.LONG_TERM}
              currency={currency}
              active={data.investmentPlan.buckets[2].active}
            />
            <MiniBucket
              term="mid_term"
              data={data.current.buckets.MID_TERM}
              currency={currency}
              active={data.investmentPlan.buckets[1].active}
            />
            <MiniBucket
              term="short_term"
              data={data.current.buckets.SHORT_TERM}
              currency={currency}
              active={data.investmentPlan.buckets[0].active}
            />
          </>
        ) : (
          <MiniBucket term="default" data={data.current.buckets.LONG_TERM} currency={currency} active={true} />
        )}
      </div>

      <Grid container className={`bgrLighter tight ${styles.box}`}>
        <Grid item xs={8} className={`${styles.mediumText}`}>
          Hotovost v peněžence
        </Grid>
        <Grid item xs={4} className={`${styles.right} ${styles.largeText}`}>
          {data.current.cash !== undefined && formatAmount(data.current.cash, currency)}
        </Grid>
      </Grid>

      <Grid container className={`bgrLighter tight ${styles.box}`}>
        <Grid item xs={6} className={styles.mediumText}>
          Celková hodnota majetku
        </Grid>
        <Grid item xs={6} className={`${styles.bigText} ${styles.right}`}>
          {data.current.balance !== undefined && formatAmount(data.current.balance, currency)}
        </Grid>
        <Grid item xs={7} className={`${styles.regularText}`}>
          Aktuální čistý zisk/ztráta (ponížený o poplatky):
        </Grid>
        <Grid item xs={5} className={`${styles.right} ${styles.mediumText}`}>
          {data.current.profit !== undefined && formatAmount(data.current.profit, currency, true)}{' '}
          {data.current.yearlyProfit !== undefined && data.current.yearlyProfit !== null && (
            <>({fractionToPercent(data.current.yearlyProfit, 2)} p. a.)</>
          )}
        </Grid>
      </Grid>

      <div className={styles.avoidPageBreak}>
        <H1>Dosavadní vývoj hodnoty majetku</H1>

        <LineGraph
          data={data.history}
          currency={currency}
          settings={{
            series: [
              { name: t('accountDetail_graph_invested'), type: 'area', fillOpacity: 1, fillColor: '#f5f5f5' },
              { name: t('accountDetail_graph_portfolioValues'), type: 'area' },
            ],
          }}
        />
      </div>

      <PageBreak />

      <H1>Nastavení investičního plánu</H1>
      <P className="noBottomMargin">
        Níže jsou uvedeny důležité údaje, na základě kterých budeme v průběhu času přeskupovat majetek mezi Nastavenými
        portfolii. Je důležité, aby tyto údaje odpovídaly vašim skutečným záměrům. V případě, že se některý z uvedených
        údajů změní, je důležité kontaktovat vašeho Poradce a společně údaje aktualizovat.
      </P>

      {rent ? (
        <Rent
          amountAtRelevantDate={data.investmentPlan.amountAtRelevantDate}
          rentApplied={data.investmentPlan.rentApplied}
          rentData={data.investmentPlan.rentData}
          currency={currency}
          inflation={data.investmentPlan.chart.inflation}
          personType={data.investmentPlan.personType}
          annual={data.type === 'YEAR'}
          actualInflation={data.actualInflation || 0}
        />
      ) : (
        <>
          <Spacer variant="quarter" />
          <H2>
            Očekávaný vývoj hodnoty majetku v čase&nbsp;
            {inflationSentence}
          </H2>

          <div className={`${styles.threeColumns} ${styles.threeColumnsTight} ${styles.center} ${styles.lines}`}>
            <Grid item xs={12}>
              Aktuální hodnota
            </Grid>
            {!data.investmentPlan.targetData.targets || data.investmentPlan.targetData.targets.length === 0 ? (
              <Grid item xs={12}>
                Očekávaná hodnota na konci horizontu
              </Grid>
            ) : (
              <Grid item xs={12}>
                Očekávaná hodnota v:
                <br />
                <strong>
                  {data.investmentPlan.chart.displayDate &&
                    sqlDateToLabel(data.investmentPlan.chart.displayDate!, 'LLLL yyyy')}
                </strong>
              </Grid>
            )}
          </div>

          <div className={`${styles.threeColumns} ${styles.center} ${styles.lines}`}>
            <Grid item xs={12} className={styles.strongText}>
              {formatAmount(data.current.balance, currency)}
            </Grid>
            {!data.investmentPlan.targetData.targets || data.investmentPlan.targetData.targets.length === 0 ? (
              <Grid item xs={12}>
                <span className={styles.strongText}>{formatAmountValue(lastValue, currency)}</span>
                <br />
                {sqlDateToLabel(lastDate, 'LLLL yyyy')}
              </Grid>
            ) : (
              <Grid item xs={12} className={styles.strongText}>
                {data.investmentPlan.chart?.valueAtDisplay?.value &&
                  formatAmountValue(data.investmentPlan.chart.valueAtDisplay.value, currency)}
              </Grid>
            )}
          </div>
        </>
      )}

      <H2>
        Očekávaný vývoj v čase&nbsp;
        {inflationSentence}
      </H2>

      <PlanChart
        planData={data.investmentPlan}
        //rentData={data.investmentPlan.rentData}
        //depositData={data.investmentPlan.depositData}
        //targetData={data.investmentPlan.targetData}
        //reserveData={data.investmentPlan.reserveData}
        //displayDate={data.investmentPlan.chart.displayDate}
        rentApplied={rent}
        currency={t(`currency_${currency}`)}
        portfolioOnly={!complete}
        xAxisTitle={
          data.investmentPlan.personType === PersonType.LEGAL
            ? ''
            : `Věk klienta
          ${data.investmentPlan.chart.metadata.partnerAgePresent ? ` / partnera` : ''}
        `
        }
        personType={data.investmentPlan.personType}
        basicColors={{
          SHORT_TERM: colorDef.test.short,
          MID_TERM: colorDef.test.mid,
          LONG_TERM: colorDef.test.long,
        }}
        colorOpacity={{
          SHORT_TERM: colorOpacityDef.test.short,
          MID_TERM: colorOpacityDef.test.mid,
          LONG_TERM: colorOpacityDef.test.long,
        }}
      />

      <Spacer variant="quarter" />

      <div className={`${styles.threeColumns}`}>
        {complete || withHistory ? (
          <>
            <PlanBucket
              term="longTerm"
              data={data.investmentPlan.buckets[2]}
              currency={currency}
              currentBalance={data.current.buckets.LONG_TERM.balance}
            />
            <PlanBucket
              term="midTerm"
              data={data.investmentPlan.buckets[1]}
              currency={currency}
              currentBalance={data.current.buckets.MID_TERM.balance}
            />
            <PlanBucket
              term="shortTerm"
              data={data.investmentPlan.buckets[0]}
              currency={currency}
              currentBalance={data.current.buckets.SHORT_TERM.balance}
            />
          </>
        ) : (
          <PlanBucket
            term="longTerm"
            data={data.investmentPlan.buckets[2]}
            currency={currency}
            profitPercent={data.history?.yearlyProfit ? data.history.yearlyProfit['K3'] : undefined}
          />
        )}
      </div>

      <PageBreak />

      <Deposits
        deposits={data.investmentPlan.depositData}
        currency={currency}
        hideSingleDeposit={true}
        annual={data.type === 'YEAR'}
        actualInflation={data.actualInflation || 0}
      />

      {data.investmentPlan.depositData.employerAllowed && data.investmentPlan.depositData.employer && (
        <GeneralDeposit
          deposit={data.investmentPlan.depositData.employer}
          currency={currency}
          annual={data.type === 'YEAR'}
          actualInflation={data.actualInflation || 0}
          type="employer"
        />
      )}

      <Reserves data={data.investmentPlan.reserveData} currency={currency} />

      <FutureDeposits
        data={data.investmentPlan.depositData.deposits}
        currency={currency}
        annual={data.type === 'YEAR'}
        actualInflation={data.actualInflation || 0}
      />
      <Targets
        data={data.investmentPlan.targetData?.targets}
        rentData={data.investmentPlan.rentData}
        currency={currency}
        personType={data.investmentPlan.personType}
        annual={data.type === 'YEAR'}
        actualInflation={data.actualInflation || 0}
      />

      <PageBreak />

      <H1>Detail celkového účtu</H1>

      <div className={styles.twoColumns}>
        <FinancesOverview
          title={`Přehled za ${data.type === 'MONTH' ? 'měsíc' : 'rok'}`}
          data={data.periodOverview}
          currency={currency}
        />
        {data.showFullOverview && data.allTimeOverview ? (
          <FinancesOverview title="Přehled od založení" data={data.allTimeOverview} currency={currency} />
        ) : (
          <div />
        )}
      </div>

      <Spacer />

      <CashMovementsTable
        data={data.transactions.cashAndSecurityMovements}
        from={data.transactions.from}
        to={data.transactions.to}
        currency={currency}
        total={data.transactions.cashAndSecurityMovementsTotal}
        mode={data.header.mode}
      />

      <InterestsTable
        data={data.transactions.interestMovements}
        total={data.transactions.interestMovementsTotal}
        currency={currency}
      />

      <FeeMovementsTable
        data={data.transactions.feeMovements}
        from={data.transactions.from}
        to={data.transactions.to}
        total={data.transactions.feeMovementsTotal}
        currency={currency}
      />

      {data.type === 'YEAR' && data.transactions.feesEffect && (
        <FeesEffect data={data.transactions.feesEffect} currency={currency} />
      )}

      {complete || withHistory ? (
        <>
          <BucketDetail
            term="longTerm"
            title="Dlouhodobé portfolio"
            bucket={data.investmentPlan.buckets[2]}
            currency={currency}
            profit={data.current.buckets.LONG_TERM.profit}
            profitPercent={data.current.buckets.LONG_TERM.yearlyProfit}
            currentBalance={data.current.buckets.LONG_TERM.balance}
            detail={data.longTermDetail}
            savingIsins={data.investmentPlan.options.specialAssets.savings}
          />
          <BucketDetail
            term="midTerm"
            title="Střednědobé portfolio"
            bucket={data.investmentPlan.buckets[1]}
            currency={currency}
            profit={data.current.buckets.MID_TERM.profit}
            profitPercent={data.current.buckets.MID_TERM.yearlyProfit}
            currentBalance={data.current.buckets.MID_TERM.balance}
            detail={data.midTermDetail}
            savingIsins={data.investmentPlan.options.specialAssets.savings}
          />
          <BucketDetail
            term="shortTerm"
            title="Krátkodobé portfolio"
            bucket={data.investmentPlan.buckets[0]}
            currency={currency}
            profit={data.current.buckets.SHORT_TERM.profit}
            profitPercent={data.current.buckets.SHORT_TERM.yearlyProfit}
            currentBalance={data.current.buckets.SHORT_TERM.balance}
            detail={data.shortTermDetail}
            savingIsins={data.investmentPlan.options.specialAssets.savings}
          />
        </>
      ) : (
        <BucketDetail
          term="longTerm"
          title="Investiční portfolio"
          bucket={data.investmentPlan.buckets[2]}
          currency={currency}
          currentBalance={data.current.buckets.LONG_TERM.balance}
          profit={data.current.buckets.LONG_TERM.profit}
          profitPercent={data.current.buckets.LONG_TERM.yearlyProfit}
          detail={data.longTermDetail}
          savingIsins={data.investmentPlan.options.specialAssets.savings}
        />
      )}

      <Glossary
        keys={[
          `totalProfit_${data.currency}`,
          'plannedDeposits',
          'requiredReserves',
          'expectedValueOfProperty',
          'requiredRent',
          'monthlyRentAmount',
          'portfolio',
          'strategy',
          'strategyRiskiness',
          'expectedReturn',
          'acceptedLoss',
          'currencyHedging',
          'automaticRebalancing',
          `profit_${data.currency}`,
          'isin',
          'investmentCategory',
          'selectedFond',
          'annualCost',
          'morningstarRating',
          'targetShare',
          `totalValueOfPropertyInPortfolios_${data.currency}`,
          'deposits',
          'withdrawals',
          'portfolioYield',
          'costs',
          'instrument',
          'date',
          'type',
          'pieces',
          'currentRate',
          'forwardRate',
          'unrealizedProfitLoss',
          'atmInstruction',
          'interests',
        ]}
        useInflation={data.investmentPlan.chart.inflation.enabled}
      />

      <Disclaimer />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_MONTHLY_STATEMENT: TemplateType = 'monthlyStatement';
const TPL_YEARLY_STATEMENT: TemplateType = 'yearlyStatement';
export { TPL_MONTHLY_STATEMENT, TPL_YEARLY_STATEMENT, Statement };
